import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { FullscreenLoader } from '@/shared/components/FullscreenLoader';
import { SessionUnavailable } from '@/shared/components/SessionUnavailable';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { authKeys, useAuth } from '@/features/auth';
import { UserStatus } from '@platform/auth';

interface AuthGuardProps {
  /** 미인증 유저를 리다이렉트할 로그인 경로 (기본: '/login') */
  loginPath?: string;
}

/**
 * 인증(Auth) 전용 Guard
 * - 미로그인 유저가 접근하면 loginPath로 이동 (현재 경로를 state로 전달)
 * - 로그인 후 원래 경로로 복귀할 수 있도록 location.state.from 보존
 *
 * 리다이렉트는 **세션이 거부된 경우(4xx)로만** 한다. 서버 장애로 /me 가 실패한 것까지
 * 미로그인으로 처리하면 멀쩡한 세션이 로그인 화면으로 튕긴다.
 *
 * 온보딩 미완료 계정은 온보딩 화면으로, 약관 개정 재동의 대상은 재동의 화면으로 보낸다. 차단 자체는
 * Gateway 가 하지만, 여기서 잡지 않으면 북마크로 들어온 사용자가 화면만 그려진 채 모든 요청이 403 이 된다.
 */
export default function AuthGuard({
  loginPath = PATHS.LOGIN,
}: AuthGuardProps) {
  const { isAuthenticated, loading, unavailable, user } = useAuth();
  const location = useLocation();
  const qc = useQueryClient();

  if (loading) return <FullscreenLoader />;

  if (unavailable) {
    return (
      <SessionUnavailable
        onRetry={() => void qc.refetchQueries({ queryKey: authKeys.me })}
      />
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginPath}
        replace
        state={{ from: location }}
      />
    );
  }

  // 온보딩 라우트는 AuthGuard 밖에 있어 여기로 되돌아오지 않는다.
  if (user?.status === UserStatus.PENDING_ONBOARDING) {
    return <Navigate to={PATHS.ONBOARDING} replace state={{ from: location }} />;
  }

  // 재동의 라우트도 AuthGuard 밖이라 되돌아오지 않는다.
  if (user?.reconsentRequired?.length) {
    return <Navigate to={PATHS.RECONSENT} replace state={{ from: location }} />;
  }

  return <Outlet />;
}
